import { Material, Matrix4 } from 'three';

import { CoordConfig, Rawdata } from '@/types';

import { Extrinsic } from '../math';
import { RawObject, RawObjectOptions } from './RawObject';
import { RawPoints } from './RawPoints';
import { Sight } from './Sight';

export class Overlays {
  /** 叠帧中的当前帧文件 */
  public currentFile: Rawdata;
  /** 所有叠帧点云的渲染对象 */
  public background: RawPoints;
  /** 叠帧中的转换矩阵集合 */
  public fileMatrixMap: Map<
    Rawdata['name'],
    {
      center: [number, number, number];
      matrixs: Required<CoordConfig>['matrixs'];
    }
  >;

  /** 起始场景的坐标系设置 */
  private sightCoord: Required<CoordConfig>;
  /** 起始帧车姿的逆矩阵，用于后续计算相对车姿 */
  private startMatInvert: Matrix4;

  constructor(startFile: Rawdata, sightCoord: Required<CoordConfig>, renderObject: RawObject) {
    const { pose } = startFile.meta.pcd ?? {};
    this.startMatInvert = pose?.length ? Extrinsic.toMatrix4(pose).invert() : new Matrix4();
    this.sightCoord = sightCoord;

    this.currentFile = startFile;

    this.fileMatrixMap = new Map();
    this.fileMatrixMap.set(this.currentFile.name, { center: [0, 0, 0], matrixs: sightCoord.matrixs });

    this.background = new RawPoints(startFile);
    this.background.needBVH = false;
    if (renderObject instanceof RawPoints) {
      this.background.intensityRange = renderObject.intensityRange;
    }
    renderObject.object.children.forEach((target) => {
      const cloned = target.clone();
      if ('material' in target && target.material instanceof Material) {
        (cloned as any).material = target.material.clone();
      }
      this.background.object.add(cloned);
    });
  }

  public async pushRawdata(file: Rawdata) {
    if (!file.meta.pcd?.pose?.length) return;

    // 查找是否已有加载好的点云对象
    const loaded = this.getRenderObjectsByName(file.name);

    if (loaded.length > 0) {
      loaded.forEach((points) => (points.visible = true));
      return;
    }

    const { matrixs, ...extraData } = this.sightCoord;
    // 根据车姿计算点云相对矩阵
    const relativeMat = Extrinsic.toMatrix4(file.meta.pcd?.pose).premultiply(this.startMatInvert);

    // 左乘矫正矩阵
    const fileToThree = relativeMat.premultiply(matrixs[1]);
    const adjustMatrixs: Required<CoordConfig>['matrixs'] = [fileToThree.clone().invert(), fileToThree];

    this.fileMatrixMap.set(file.name, {
      center: relativeMat.elements.slice(12, 15) as [number, number, number],
      matrixs: adjustMatrixs,
    });

    const newCoord: Required<CoordConfig> = { ...extraData, matrixs: adjustMatrixs };
    await this.background.load(file, { coord: newCoord });

    this.background.object.traverse((obj) => obj.layers.set(Sight.RAW_OBJECT_LAYER));
  }

  /**
   * 更新叠帧中的当前帧
   * @param name 文件名
   */
  public updateCurrent(name: Rawdata) {
    this.currentFile = name;
  }

  /**
   * 获取叠帧中当前帧的转换矩阵
   * @returns
   */
  public getCurrentMatrixs(): Required<CoordConfig>['matrixs'] | undefined {
    return this.fileMatrixMap.get(this.currentFile.name)?.matrixs;
  }

  /**
   * 获取叠帧中当前帧的转换矩阵
   * @returns
   */
  public getCurrentCenter(): [number, number, number] | undefined {
    return this.fileMatrixMap.get(this.currentFile.name)?.center;
  }

  public showRawdata(name: string) {
    // 查找是否已有加载好的点云对象
    const loaded = this.getRenderObjectsByName(name);
    if (loaded.length > 0) {
      loaded.forEach((points) => (points.visible = true));
    }
    return loaded;
  }

  public hideRawdata(name: string) {
    // 查找是否已有加载好的点云对象
    const loaded = this.getRenderObjectsByName(name);
    if (loaded.length > 0) {
      loaded.forEach((points) => (points.visible = false));
    }
    return loaded;
  }

  public getRenderObjectsByName(name: string) {
    return this.background.object.children.filter((points) => points.name === name);
  }

  public updateRenderOptions(options: RawObjectOptions) {
    const overlayOptions = {
      ...options,
      // TODO: 目前透明度仍然有问题，似乎不应该在这里设置
      // opacity: 0.35,
    };
    console.log(overlayOptions);

    // 叠帧时，非当前帧的点云大小为设置的 50%（由于透明度有问题，暂时以点云大小差别来标识）
    typeof overlayOptions.pointSize === 'number' && (overlayOptions.pointSize *= 0.5);

    this.background.updateRenderOptions(overlayOptions);
  }
}
